var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import TextField from '@mui/material/TextField';
export const CURRENCY_DECIMAL_SCALE = 2;
// This regex filters out any symbol that is not a numeric character or negative sign
const NON_NUMERIC_OR_NEGATIVE = /[^\d-]/g;
// This regex filters out any symbol that is not a numeric character
const NON_NUMERIC = /[^\d]/g;
// this stops the input from changing the value on scroll
export const preventOnWheelChange = (e) => {
    const target = e.target;
    target.blur();
    e.stopPropagation();
};
/**
 * Converts a dollar input string (e.g. "$1234.56") to a number representing the same price in cents (e.g. 123456).
 */
function convertDollarInputStringToPriceInCents(dollarInputValue, allowNegative) {
    // First add missing cent zeroes
    let valueWithMissingCentsDecimals = dollarInputValue;
    const decimalIndex = dollarInputValue.indexOf('.');
    if (decimalIndex !== -1) {
        const numMissingCentsDecimals = 2 - (dollarInputValue.length - decimalIndex - 1);
        valueWithMissingCentsDecimals += '0'.repeat(numMissingCentsDecimals);
    }
    else {
        valueWithMissingCentsDecimals += '00';
    }
    // Then convert to number
    const value = parseInt(valueWithMissingCentsDecimals.replace(
    // Replace all non-numeric characters with empty string. If we allow negative, do not filter out the negative sign
    allowNegative ? NON_NUMERIC_OR_NEGATIVE : NON_NUMERIC, ''));
    const priceInCentsValue = isNaN(value) ? 0 : value;
    return priceInCentsValue;
}
function formatPriceInCentsAsDollarString(priceInCents) {
    if (!priceInCents) {
        return '0.00';
    }
    return (priceInCents / 100).toFixed(2);
}
/**
 * Currency Input for showing/editing prices. Automatically handles conversions to cents (callbacks are called with cent-values).
 *
 * Note: You can set EITHER `priceInCents` (controlled) or `initialPriceInCents` (uncontrolled) props, but not both.
 */
export const CurrencyInput = (_a) => {
    var _b;
    var { allowEmpty, onChange, onBlur, onFocus, initialPriceInCents, priceInCents, name, allowNegative, dataTestId } = _a, rest = __rest(_a, ["allowEmpty", "onChange", "onBlur", "onFocus", "initialPriceInCents", "priceInCents", "name", "allowNegative", "dataTestId"]);
    const isControlledInput = priceInCents !== undefined;
    const [hasFocus, setHasFocus] = useState(false);
    const [internalPriceInCents, setInternalPriceInCents] = useState((_b = initialPriceInCents !== null && initialPriceInCents !== void 0 ? initialPriceInCents : priceInCents) !== null && _b !== void 0 ? _b : null);
    const [inputStringValue, setInputStringValue] = useState(internalPriceInCents
        ? formatPriceInCentsAsDollarString(internalPriceInCents)
        : allowEmpty
            ? ''
            : '0');
    // Update internalPriceInCents if priceInCents changes (controlled input)
    useEffect(() => {
        if (isControlledInput) {
            setInternalPriceInCents(priceInCents);
            if (!hasFocus) {
                setInputStringValue(formatPriceInCentsAsDollarString(priceInCents));
            }
        }
    }, [priceInCents]);
    if ((initialPriceInCents !== undefined && priceInCents !== undefined) ||
        (initialPriceInCents === undefined && priceInCents === undefined)) {
        throw new Error(`CurrencyInput must be provided with either initialPriceInCents or priceInCents, but not both (initialPriceInCents: ${initialPriceInCents}, priceInCents: ${priceInCents}).`);
    }
    const handleChange = (e) => {
        setInputStringValue(e.target.value);
        if (allowEmpty && e.target.value.trim() === '') {
            setInternalPriceInCents(null);
            onChange === null || onChange === void 0 ? void 0 : onChange(null);
            return;
        }
        const priceInCentsValue = convertDollarInputStringToPriceInCents(e.target.value, allowNegative);
        setInternalPriceInCents(priceInCentsValue);
        onChange === null || onChange === void 0 ? void 0 : onChange(priceInCentsValue);
    };
    const handleBlur = (e) => {
        setHasFocus(false);
        if (allowEmpty && e.target.value.trim() === '') {
            setInternalPriceInCents(null);
            onBlur === null || onBlur === void 0 ? void 0 : onBlur(null);
            return;
        }
        const priceInCentsValue = convertDollarInputStringToPriceInCents(e.target.value, allowNegative);
        setInternalPriceInCents(priceInCentsValue);
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(priceInCentsValue);
        // Exiting the input with no value resets the input to 0
        setInputStringValue(formatPriceInCentsAsDollarString(priceInCentsValue));
    };
    const handleFocus = (e) => {
        setHasFocus(true);
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
    };
    const handleKeyDown = (e) => {
        // Prevent multiple decimal points
        const el = e.target;
        if (e.key === '.' && el.value.includes('.')) {
            e.preventDefault();
            return;
        }
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(NumericFormat, Object.assign({ customInput: TextField }, rest, { prefix: "$", onBlur: handleBlur, onFocus: handleFocus, onKeyDown: handleKeyDown, thousandSeparator: true, onChange: handleChange, onWheel: preventOnWheelChange, decimalScale: CURRENCY_DECIMAL_SCALE, allowNegative: !!allowNegative, value: inputStringValue, "data-testid": dataTestId })),
        !isControlledInput && !!name && (React.createElement("input", { type: "hidden", name: name, value: internalPriceInCents !== null && internalPriceInCents !== void 0 ? internalPriceInCents : '' }))));
};
